import React, { useMemo } from 'react';

import InfoIcon from '@atlaskit/icon/glyph/info';
import { B200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { InfoFlag } from '@atlassian/jira-flags';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button';
import { useIntl } from '@atlassian/jira-intl';

import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { CUSTOM_THEME_ADMIN_FLAG_ID } from '../common/constants';
import messages from './messages';

export const CUSTOM_THEME_ADMIN_FLAG_TEST_ID = 'custom-theme-admin-flag-test-id';
export const REVERT_HREF = '/secure/admin/LookAndFeel!default.jspa';
const HELP_ARTICLE_ID = '1QC8AUgw7MAmoVtoYXQDxM';

type Props = {
	onDismiss: () => void;
	analyticsAttributes: {
		backgroundColor: string;
		highlightColor: string;
	};
};

const CustomThemeAdminFlag = ({ onDismiss, analyticsAttributes }: Props) => {
	const { formatMessage } = useIntl();
	const onOpenInProductHelpArticle = useHelpPanelLink();

	return useMemo(
		() => (
			<InfoFlag
				icon={<InfoIcon primaryColor={token('color.icon.information', B200)} label="" />}
				testId={CUSTOM_THEME_ADMIN_FLAG_TEST_ID}
				id={CUSTOM_THEME_ADMIN_FLAG_ID}
				title={formatMessage(messages.flagHeader)}
				description={formatMessage(messages.flagBody)}
				onDismissed={(_, analyticsEvent) => {
					onDismiss();
					fireUIAnalytics(
						analyticsEvent,
						'customTopNavAdminFlagWarningDismissButton',
						analyticsAttributes,
					);
				}}
				actions={[
					{
						content: formatMessage(messages.revertButton),
						href: REVERT_HREF,
						target: '_blank',
						onClick: (_, analyticsEvent) => {
							onDismiss();
							fireUIAnalytics(
								analyticsEvent,
								'customTopNavAdminFlagWarningRevertButton',
								analyticsAttributes,
							);
						},
					},
					{
						content: formatMessage(messages.learnMoreButton),
						onClick: (_, analyticsEvent) => {
							fireUIAnalytics(
								analyticsEvent,
								'customTopNavAdminFlagWarningMoreButton',
								analyticsAttributes,
							);
							onOpenInProductHelpArticle?.(HELP_ARTICLE_ID);
						},
					},
				]}
			/>
		),
		[formatMessage, onDismiss, onOpenInProductHelpArticle, analyticsAttributes],
	);
};

export default CustomThemeAdminFlag;
